// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Golden Hammer Progressive Jackpot Slot
// 5-reel, 20-payline with progressive jackpot pool
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { DEFAULT_SYMBOLS, WILD_SYMBOL, SCATTER_SYMBOL, mapToGrid, buildReelStrip } from '../../engine/ReelMapper';
import { calculateWins, PAYLINES_5x3 } from '../../engine/PaytableCalculator';
import { useCasino } from '../../context/CasinoShell';
import SlotCabinet from '../SlotCabinet';

const HAMMER_SYMBOLS = [
  { id: 'golden',   emoji: '🔨', weight: 1,  payout: { 3: 100, 4: 500, 5: 0 }, name: 'Golden Hammer', isJackpot: true },
  { id: 'forge',    emoji: '🔥', weight: 3,  payout: { 3: 30, 4: 120, 5: 600 }, name: 'Forge' },
  { id: 'anvil',    emoji: '⚒️',  weight: 4,  payout: { 3: 20, 4: 80, 5: 400 }, name: 'Anvil' },
  { id: 'diamond',  emoji: '💎', weight: 5,  payout: { 3: 12, 4: 50, 5: 200 }, name: 'Diamond' },
  { id: 'crown',    emoji: '👑', weight: 6,  payout: { 3: 8, 4: 35, 5: 120 }, name: 'Crown' },
  { id: 'ring',     emoji: '💍', weight: 8,  payout: { 3: 5, 4: 20, 5: 60 }, name: 'Ring' },
  { id: 'coin',     emoji: '🪙', weight: 10, payout: { 3: 3, 4: 12, 5: 35 }, name: 'Coin' },
  { id: 'gem',      emoji: '🔶', weight: 12, payout: { 3: 2, 4: 6, 5: 18 }, name: 'Gem' },
  { ...WILD_SYMBOL, weight: 2 },
  { ...SCATTER_SYMBOL, weight: 2 },
];

const ACTIVE_PAYLINES = PAYLINES_5x3.slice(0, 20);

export default function GoldenHammer() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  const [grid, setGrid] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [betPerLine, setBetPerLine] = useState(0.05);
  const [lastWin, setLastWin] = useState(null);
  const [lastProof, setLastProof] = useState(null);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [message, setMessage] = useState('CHASE THE JACKPOT');
  const [winAnimation, setWinAnimation] = useState(false);
  const [jackpotPool, setJackpotPool] = useState(10000.00); // Progressive starting at $10K

  const totalBet = betPerLine * ACTIVE_PAYLINES.length;

  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
    const reelStrip = buildReelStrip(HAMMER_SYMBOLS);
    const initialGrid = [];
    for (let r = 0; r < 5; r++) {
      const reel = [];
      for (let row = 0; row < 3; row++) {
        reel.push(reelStrip[Math.floor(Math.random() * reelStrip.length)]);
      }
      initialGrid.push(reel);
    }
    setGrid(initialGrid);
  }, []);

  // Jackpot ticking animation
  useEffect(() => {
    const timer = setInterval(() => {
      setJackpotPool(prev => prev + 0.01 + Math.random() * 0.05);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const spin = useCallback(async () => {
    if (spinning || isExcluded()) return;

    const betResult = placeBet(totalBet, 'golden-hammer');
    if (!betResult.success) { setMessage(betResult.reason.toUpperCase()); return; }

    // Feed jackpot pool (2% of every bet)
    setJackpotPool(prev => prev + totalBet * 0.02);

    setSpinning(true);
    setLastWin(null);
    setWinAnimation(false);
    setMessage('SPINNING...');

    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    const result = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();
    const newGrid = mapToGrid(result.resultValues, 5, 3, HAMMER_SYMBOLS);

    // Animate
    const reelStrip = buildReelStrip(HAMMER_SYMBOLS);
    for (let frame = 0; frame < 14; frame++) {
      await new Promise(resolve => setTimeout(resolve, 70));
      const tempGrid = [];
      for (let r = 0; r < 5; r++) {
        if (frame >= 9 + r) {
          tempGrid.push(newGrid[r]);
        } else {
          const reel = [];
          for (let row = 0; row < 3; row++) {
            reel.push(reelStrip[Math.floor(Math.random() * reelStrip.length)]);
          }
          tempGrid.push(reel);
        }
      }
      setGrid(tempGrid);
    }
    setGrid(newGrid);

    // Check for jackpot (5 Golden Hammers across middle payline)
    const middleLine = newGrid.map(reel => reel[1]);
    const jackpotHit = middleLine.every(s => s.id === 'golden' || s.isWild);

    let winResult = calculateWins(newGrid, betPerLine, ACTIVE_PAYLINES);

    if (jackpotHit) {
      const jackpotWin = jackpotPool;
      winResult = { ...winResult, totalPayout: winResult.totalPayout + jackpotWin, isWin: true };
      setMessage(`🏆 JACKPOT! $${jackpotWin.toFixed(2)}!!!`);
      setJackpotPool(10000.00); // Reset jackpot
    }

    if (winResult.isWin) {
      recordWin(winResult.totalPayout, 'golden-hammer', proof.resultHash);
      setLastWin(winResult);
      setWinAnimation(true);
      if (!jackpotHit) setMessage(`WIN! $${winResult.totalPayout.toFixed(2)}`);
      setTimeout(() => setWinAnimation(false), 3000);
    } else {
      recordLoss(totalBet, 'golden-hammer', proof.resultHash);
      setMessage('SPIN AGAIN');
    }

    logFairness({ gameId: 'golden-hammer', ...proof, betAmount: totalBet, winAmount: winResult.totalPayout });
    setLastProof(proof);
    setSpinning(false);
    const nextCommitment = await engineRef.current.createCommitment();
    setCommitmentHash(nextCommitment.commitmentHash);
  }, [spinning, totalBet, betPerLine, placeBet, recordWin, recordLoss, logFairness, isExcluded, jackpotPool]);

  return (
    <SlotCabinet title="GOLDEN HAMMER" icon="🔨" theme="neon"
      stats={[
        { label: 'BALANCE', value: `$${forgeBalance.toFixed(2)}` },
        { label: 'JACKPOT', value: `$${jackpotPool.toFixed(2)}` },
        { label: 'BET', value: `$${totalBet.toFixed(2)}` },
      ]}
    >
      <div style={{ padding: 8 }}>
        {/* Jackpot Ticker */}
        <div style={gs.jackpotBar}>
          <span style={gs.jackpotLabel}>🏆 PROGRESSIVE JACKPOT</span>
          <span style={gs.jackpotValue}>${jackpotPool.toFixed(2)}</span>
        </div>

        <div style={gs.commitBar}>
          <span style={gs.commitLabel}>HASH:</span>
          <span style={gs.commitHash}>{commitmentHash?.slice(0, 16) || '...'}</span>
        </div>

        {/* 5x3 Grid */}
        <div style={gs.reelContainer}>
          {grid?.map((reel, r) => (
            <div key={r} style={gs.reel}>
              {reel.map((sym, row) => (
                <div key={row} style={{
                  ...gs.cell,
                  ...(row === 1 ? gs.cellMiddle : {}),
                  ...(winAnimation && lastWin?.wins?.some(w => w.positions?.some(p => p.reel === r && p.row === row)) ? gs.cellWin : {}),
                }}>
                  <span style={{ fontSize: 26 }}>{sym.emoji}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{ ...gs.message, color: winAnimation ? '#00FF88' : '#00FF88' }}>{message}</div>

        <div style={gs.controls}>
          <div style={gs.betControls}>
            <button style={gs.betBtn} onClick={() => setBetPerLine(prev => Math.max(0.01, prev - 0.01))} disabled={spinning}>-</button>
            <span style={gs.betVal}>${betPerLine.toFixed(2)}</span>
            <button style={gs.betBtn} onClick={() => setBetPerLine(prev => Math.min(10, prev + 0.01))} disabled={spinning}>+</button>
          </div>
          <button style={{ ...gs.spinBtn, opacity: spinning ? 0.5 : 1 }} onClick={spin} disabled={spinning || forgeBalance < totalBet}>
            {spinning ? '⏳' : '🔨 SPIN'}
          </button>
        </div>

        {lastProof && (
          <button style={gs.verifyBtn} onClick={async () => {
            const r = await ProvablyFairEngine.verify(lastProof);
            alert(r.valid ? 'VERIFIED FAIR!' : r.reason);
          }}>VERIFY LAST SPIN</button>
        )}

        <div style={gs.jackpotNote}>
          5x 🔨 on center line = JACKPOT | 2% of every bet feeds the pool
        </div>
      </div>
    </SlotCabinet>
  );
}

const gs = {
  jackpotBar: { textAlign: 'center', padding: '8px', background: '#001a0a', border: '2px solid #00FF88', borderRadius: 8, marginBottom: 6 },
  jackpotLabel: { display: 'block', fontSize: 8, color: '#00FF88', fontWeight: 700, letterSpacing: 2 },
  jackpotValue: { fontSize: 22, fontWeight: 900, color: '#00FF88', fontFamily: "'Courier New', monospace", textShadow: '0 0 12px #00FF88' },
  commitBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '4px 6px', background: '#0a0a0a', borderRadius: 4, marginBottom: 6 },
  commitLabel: { fontSize: 7, color: '#555', fontWeight: 700 },
  commitHash: { fontSize: 8, color: '#00FF88', fontFamily: 'monospace' },
  reelContainer: { display: 'flex', justifyContent: 'center', gap: 3, padding: '8px 0', background: 'linear-gradient(180deg, #0a1a0a 0%, #000 50%, #0a1a0a 100%)', borderRadius: 6, border: '2px solid #00FF8833' },
  reel: { display: 'flex', flexDirection: 'column', gap: 2 },
  cell: { width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', borderRadius: 4, border: '1px solid #1a1a1a' },
  cellMiddle: { border: '2px solid #00FF8844', background: '#001a0a' },
  cellWin: { border: '2px solid #00FF88', boxShadow: '0 0 10px #00FF8844' },
  message: { textAlign: 'center', fontSize: 12, fontWeight: 900, letterSpacing: 2, padding: '6px 0' },
  controls: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '6px 0' },
  betControls: { display: 'flex', alignItems: 'center', gap: 4 },
  betBtn: { width: 24, height: 24, borderRadius: 4, border: '1px solid #00FF88', background: '#001a0a', color: '#00FF88', fontSize: 14, fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  betVal: { fontSize: 12, color: '#00FF88', fontWeight: 900, minWidth: 45, textAlign: 'center' },
  spinBtn: { padding: '10px 24px', borderRadius: 8, border: '2px solid #00FF88', background: 'linear-gradient(180deg, #00FF88 0%, #00AA55 100%)', color: '#000', fontSize: 13, fontWeight: 900, letterSpacing: 1, cursor: 'pointer' },
  verifyBtn: { display: 'block', width: '100%', marginTop: 6, padding: '6px', borderRadius: 4, border: '1px solid #333', background: 'none', color: '#00FF88', fontSize: 9, fontWeight: 700, cursor: 'pointer', textAlign: 'center' },
  jackpotNote: { textAlign: 'center', fontSize: 8, color: '#555', marginTop: 6 },
};
